import { useEffect } from 'react'
import { NavLink, useRouteError } from 'react-router-dom'
import { motion } from 'framer-motion'
import styled from 'styled-components'

import usePageMatch from '@/shared/hooks/usePageMatch'
import { captureError } from '@/shared/sentry'
import MetaTag from '@/shared/seo/MetaTag'
import { Noto_b_size, Noto_r_size } from '@/styles/font/NotoSansKrFonts.ts'
import { mediaTablet } from '@/styles/mediaQuery.ts'

const ErrorBoundaryPage = () => {
  const error = useRouteError()

  useEffect(() => {
    if (!error) return
    if (!(error instanceof Error)) return

    captureError(error)
  }, [error])

  const { matchNotFound } = usePageMatch()
  return (
    <>
      <MetaTag
        title={'Page not found - 휴대폰분실보호'}
        description={
          '페이지 주소가 잘못 입력되었거나, 주소가 변경 또는 삭제되어 요청하신 페이지를 찾을 수 없습니다.'
        }
      />
      {matchNotFound && (
        <NotFoundWrap
          initial={{ y: '20px', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 180, damping: 10 }}
        >
          <NotFoundInner>
            <h1>
              404
              <br /> 페이지를 찾을수 없습니다.
            </h1>
            <p>
              페이지 주소가 잘못 입력되었거나, 주소가 변경 또는 삭제되어
              <br /> 요청하신 페이지를 찾을 수 없습니다.
            </p>
          </NotFoundInner>
          <NotFoundNavWrap>
            <NotFoundNavLink to={'/'} aria-label="메인페이지로 이동하기">
              🏠 메인페이지
            </NotFoundNavLink>
            <NotFoundNavLink to={'center'} aria-label="고객센터로 이동하기">
              ❓ 고객센터
            </NotFoundNavLink>
          </NotFoundNavWrap>
        </NotFoundWrap>
      )}
    </>
  )
}

export default ErrorBoundaryPage
const NotFoundWrap = styled(motion.div)`
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 366px;
  margin: 326px auto 246px;
  box-sizing: border-box;
  ${mediaTablet} {
    padding: 0 20px;
    margin: 205px auto 135px;
  }
`
const NotFoundInner = styled.div`
  display: block;
  width: 100%;
  text-align: center;
  > h1 {
    ${Noto_b_size(24)};
    line-height: 1.4;
    color: rgb(26, 26, 26);
    word-break: keep-all;
  }
  > p {
    ${Noto_r_size(14)};
    line-height: 1.6;
    color: rgb(102, 102, 102);
    margin-top: 20px;
    word-break: keep-all;
  }
`
const NotFoundNavWrap = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  margin-top: 30px;
`
const NotFoundNavLink = styled(NavLink)`
  width: 100%;
  max-width: 300px;
  border: 1px solid rgb(204, 204, 204);
  box-sizing: border-box;
  padding: 10px 16px;
  border-radius: 8px;
  text-align: center;
  ${Noto_r_size(16)};
  color: rgb(77, 77, 77);
  transition: 0.3s;
  &:hover {
    border-color: rgb(77, 77, 77);
    color: rgb(0, 0, 0);
  }
`
